import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';
import type { ApiResponse, TokenResponse } from './types';

export const ACCESS_TOKEN_KEY = 'gency03.accessToken';
const REFRESH_TOKEN_KEY = 'gency03.refreshToken';
/** zustand persist 가 사용자 정보를 담아 두는 키 (authStore 와 공유). */
export const AUTH_STATE_KEY = 'gency03.auth';

export const tokenStorage = {
  getAccessToken: () => localStorage.getItem(ACCESS_TOKEN_KEY),
  getRefreshToken: () => localStorage.getItem(REFRESH_TOKEN_KEY),
  save(accessToken: string, refreshToken: string) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  },
  clear() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    // 토큰만 지우고 사용자 정보를 남겨 두면 "화면은 관리자, 토큰은 다른 계정" 상태가 된다.
    localStorage.removeItem(AUTH_STATE_KEY);
  },
};

/** 서버가 내려준 code/message 를 그대로 담는 에러. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly status?: number,
    readonly errors?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// 개발 서버에서는 vite 프록시를 타도록 상대 경로를 쓴다.
const baseURL = import.meta.env.DEV ? '' : (import.meta.env.VITE_API_BASE_URL ?? '');

const instance: AxiosInstance = axios.create({
  baseURL,
  timeout: 15_000,
  headers: { 'Content-Type': 'application/json' },
});

instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStorage.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** 동시에 401 이 여러 개 떠도 리프레시는 한 번만 하도록 묶어 둔다. */
let refreshPromise: Promise<string> | null = null;

async function refreshAccessToken(): Promise<string> {
  const refreshToken = tokenStorage.getRefreshToken();
  if (!refreshToken) {
    throw new ApiError('로그인이 필요합니다.', 'UNAUTHORIZED', 401);
  }
  const { data } = await axios.post<ApiResponse<TokenResponse>>(
    `${baseURL}/api/auth/refresh`,
    { refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  );
  if (!data.success || !data.data) {
    throw new ApiError(data.message ?? '토큰 재발급에 실패했습니다.', data.code ?? 'INVALID_TOKEN', 401);
  }
  tokenStorage.save(data.data.accessToken, data.data.refreshToken);
  return data.data.accessToken;
}

instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const original = error.config as InternalAxiosRequestConfig & { _retried?: boolean };
    const status = error.response?.status;
    const body = error.response?.data;

    const isRefreshCall = original?.url?.includes('/api/auth/refresh');
    if (status === 401 && original && !original._retried && !isRefreshCall) {
      original._retried = true;
      try {
        refreshPromise ??= refreshAccessToken().finally(() => {
          refreshPromise = null;
        });
        const newToken = await refreshPromise;
        original.headers.Authorization = `Bearer ${newToken}`;
        return instance(original);
      } catch {
        tokenStorage.clear();
        // 로그인 페이지로 보낸다. 이미 로그인 화면이면 그대로 둔다.
        if (!location.pathname.startsWith('/login')) {
          location.href = `/login?redirect=${encodeURIComponent(location.pathname + location.search)}`;
        }
      }
    }

    throw new ApiError(
      body?.message ?? error.message ?? '요청 처리 중 오류가 발생했습니다.',
      body?.code ?? 'NETWORK_ERROR',
      status,
      body?.errors,
    );
  },
);

/** ApiResponse 봉투를 벗겨 data 만 돌려준다. */
async function unwrap<T>(promise: Promise<{ data: ApiResponse<T> }>): Promise<T> {
  const { data } = await promise;
  if (!data.success) {
    throw new ApiError(data.message ?? '요청에 실패했습니다.', data.code ?? 'UNKNOWN', undefined, data.errors);
  }
  return data.data as T;
}

export const api = {
  get: <T>(url: string, config?: AxiosRequestConfig) => unwrap<T>(instance.get(url, config)),
  post: <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
    unwrap<T>(instance.post(url, body, config)),
  put: <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
    unwrap<T>(instance.put(url, body, config)),
  patch: <T>(url: string, body?: unknown, config?: AxiosRequestConfig) =>
    unwrap<T>(instance.patch(url, body, config)),
  delete: <T>(url: string, config?: AxiosRequestConfig) => unwrap<T>(instance.delete(url, config)),
};

export default instance;
